import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CellAnnouncements } from "./CellAnnouncements";
import { HostGroupText } from "./HostGroupText";
import { CellTextButton } from "./CellTextButton";
import { CellCallButton } from "./CellCallButton";
import { Crown, MapPin, CalendarClock, Users, Home, ClipboardList, Copy, CheckCircle2, Hourglass } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const INPUT = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-3 py-2.5 text-sm";
const EMPTY = { postcode: "", address: "", phone: "", availability: "", willing_to_host: false, notes: "" };

const initials = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();

const GroupView = ({ g, onChange }) => (
  <div className="space-y-5">
  <CellAnnouncements g={g} onChange={onChange} />
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="my-cell-group-details">
    <div className={`${CARD} p-6 lg:col-span-1 space-y-4`} data-testid="my-cell-host-card">
      <h3 className="font-head text-lg font-semibold flex items-center gap-2"><Home className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Where we meet</h3>
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--sj-text-3)" }}>Host</div>
        <div className="mt-1 flex items-center gap-2 font-semibold" data-testid="my-cell-host-name"><Crown className="h-4 w-4" style={{ color: "#D97706" }} /> {g.host_name}{g.is_host && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#FEF6D8", color: "#8a6a00" }}>That's you</span>}</div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--sj-text-3)" }}>Hosting address</div>
        <div className="mt-1 flex items-start gap-2 text-sm" data-testid="my-cell-host-address"><MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-primary)" }} />{g.host_address || <span style={{ color: "var(--sj-text-3)" }}>Your host will share the address with you.</span>}</div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--sj-text-3)" }}>Meeting day</div>
        <div className="mt-1 flex items-center gap-2 text-sm font-medium" data-testid="my-cell-meeting-day"><CalendarClock className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> {g.meeting_day || "To be confirmed"}</div>
      </div>
    </div>
    <div className={`${CARD} p-6 lg:col-span-2`} data-testid="my-cell-members">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head text-lg font-semibold flex items-center gap-2"><Users className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> People in your group</h3>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>{g.member_count} people</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {g.members.map((m, i) => (
          <div key={i} data-testid={`my-cell-member-${i}`} className="flex items-center gap-3 rounded-xl border px-3 py-2.5" style={{ borderColor: m.is_me ? "var(--sj-primary)" : "var(--sj-border)", background: m.is_me ? "#F2F8F5" : "#fff" }}>
            <span className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={m.role === "Host" ? { background: "var(--sj-accent)", color: "#063B30" } : { background: "#E3F1EC", color: "var(--sj-primary)" }}>{initials(m.name)}</span>
            <span className="text-sm font-medium truncate">{m.name}</span>
            {m.role === "Host" && <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF6D8", color: "#8a6a00" }}>Host</span>}
            {m.is_me && m.role !== "Host" && <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>You</span>}
            {m.record_id && <span className="ml-auto flex items-center"><CellCallButton recordId={m.record_id} name={m.name} /><CellTextButton recordId={m.record_id} name={m.name} /></span>}
          </div>
        ))}
      </div>
      <p className="text-xs mt-4" style={{ color: "var(--sj-text-3)" }}>{g.is_host ? "As host you can call or text your members through the app — their numbers stay private." : "Contact details are kept private. Your host or cell leader will get in touch about meetings."}</p>
      {g.is_host && <HostGroupText />}
    </div>
  </div>
  </div>
);

export const MyCellGroupPanel = ({ withHero = false }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, g] = await Promise.all([api.get("/portal/cell/signup"), api.get("/portal/cell/my-group")]);
      setData({ ...s.data, group: g.data });
      if (s.data.signup) setForm({ ...EMPTY, ...s.data.signup, willing_to_host: !!s.data.signup.willing_to_host });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.postcode.trim()) { toast.error("Please enter your postcode."); return; }
    setSaving(true);
    try {
      await api.post("/portal/cell/signup", form);
      toast.success(data?.signed_up ? "Your cell group details were updated" : "Thanks! Your cell group response has been sent");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const shareLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/cell-response`); toast.success("Form link copied — share it with a friend"); }
    catch { toast.error("Could not copy"); }
  };

  if (!data) return <div className={`${CARD} p-8 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>;
  const g = data.group || {};
  const approved = g.status === "approved";
  const scrollToForm = () => document.getElementById("cell-response-section")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="space-y-6" data-testid="my-cell-group-panel">
      {withHero && (
        <div className="sj-hero p-7 sm:p-9" data-testid="my-cell-hero">
          <div className="sj-eyebrow">Streams of Joy Erith London · My Cell Group</div>
          {approved ? (
            <>
              <h1 className="font-head text-3xl sm:text-4xl font-extrabold mt-2">You're in <span style={{ color: "var(--sj-accent)" }}>{g.group_name}</span></h1>
              <p className="mt-2 text-white/85">{g.meeting_day ? `Meets ${g.meeting_day}` : "Meeting day to be confirmed"} · hosted by {g.host_name} · {g.member_count} people</p>
            </>
          ) : (
            <>
              <h1 className="font-head text-3xl sm:text-4xl font-extrabold mt-2">{g.status === "pending" ? "You're on the list" : "Join a cell group"}</h1>
              <p className="mt-2 text-white/85 max-w-xl">{g.status === "pending" ? "Thanks for your response — we're finding the right group near you. You'll see your group here once it's confirmed." : "Cell groups meet locally during the week to pray, study and grow together. Tell us where you live and we'll connect you with a group near you."}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button data-testid="my-cell-hero-form-btn" onClick={scrollToForm} className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold" style={{ background: "var(--sj-accent)", color: "#063B30" }}>
                  <ClipboardList className="h-4 w-4" /> {g.status === "pending" ? "Update my response" : "Complete the response form"}
                </button>
                <button data-testid="my-cell-share-link" onClick={shareLink} className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10">
                  <Copy className="h-4 w-4" /> Share form with a friend
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {approved && <GroupView g={g} onChange={load} />}
      {!withHero && g.status === "pending" && (
        <div className={`${CARD} p-4 text-sm flex items-center gap-2`} style={{ color: "var(--sj-text-2)" }} data-testid="my-cell-pending"><Hourglass className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> Thanks for signing up — you'll be placed into a cell group soon.</div>
      )}

      {!approved && (
        <div id="cell-response-section" className={`${CARD} p-6 scroll-mt-24`} data-testid="my-cell-form">
          <h3 className="font-head text-lg font-semibold flex items-center gap-2">
            {data.signed_up ? <CheckCircle2 className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> : <ClipboardList className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />}
            {data.signed_up ? "Your cell group response" : "Cell Group Response Form"}
          </h3>
          <p className="text-sm mt-1 mb-4" style={{ color: "var(--sj-text-2)" }}>{data.signed_up ? "We have your response. You can update it any time." : "Signed in as yourself — just add where you live and when you can meet."}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold" style={{ color: "var(--sj-text-2)" }}>Postcode *</label><input data-testid="portal-cell-postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Example: DA8 1AB" className={INPUT} /></div>
            <div><label className="text-xs font-semibold" style={{ color: "var(--sj-text-2)" }}>Phone</label><input data-testid="portal-cell-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={INPUT} /></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold" style={{ color: "var(--sj-text-2)" }}>Address (optional)</label><input data-testid="portal-cell-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={INPUT} /></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold" style={{ color: "var(--sj-text-2)" }}>When can you meet?</label><input data-testid="portal-cell-availability" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="e.g. Tuesday or Thursday evenings" className={INPUT} /></div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
            <input data-testid="portal-cell-host" type="checkbox" checked={form.willing_to_host} onChange={(e) => setForm({ ...form, willing_to_host: e.target.checked })} className="accent-[#0B5E4E]" />
            I am open to discussing hosting a cell group.
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="portal-cell-submit" disabled={saving} onClick={submit} className="rounded-xl px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
              {saving ? "Saving…" : data.signed_up ? "Update my response" : "Submit response"}
            </button>
            {!withHero && <Link to="/my-cell-group" data-testid="portal-cell-open-page" className="text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Open My Cell Group</Link>}
          </div>
        </div>
      )}
    </div>
  );
};
